import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { ToolComponent } from '@igo2/common/tool';
import { DrawComponent, IgoMap } from '@igo2/geo';
import { DrawState, MapState } from '@igo2/integration';

/** Types repris de DrawState, pour rester alignés sur igo2. */
type Stores = DrawState['stores'];
type DrawControls = DrawState['drawControls'];
type CoucheActive = DrawState['activeDrawingLayer'];

/**
 * Outil de dessin — remplace celui d'igo2 (même nom « draw », donc aucun
 * contexte à modifier).
 *
 * igo2 20 a fait de `stores` un model() de DrawComponent, qu'il complète par
 * stores.update(s => s.concat(...)), donc un nouveau tableau. Son outil
 * (igo-drawing-tool) ne lie que [stores] sans écouter storesChange : DrawState ne
 * recevait jamais la liste créée. En revenant à l'outil, DrawComponent repartait
 * d'une liste vide : nouvelle couche, liste des dessins vide, l'ancienne couche
 * restant sur la carte. En 18.x, stores.push() modifiait le tableau partagé.
 * Seule différence avec l'outil d'igo2 : la liaison double [(stores)].
 * À retirer quand igo2 corrigera son outil.
 */
@ToolComponent({
  name: 'draw',
  title: 'igo.integration.tools.draw',
  icon: 'stylus_note'
})
@Component({
  selector: 'sigt-drawing-tool',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DrawComponent],
  template: `
    <igo-draw
      [(stores)]="stores"
      [map]="map"
      (layersIDEvent)="addLayersID($event)"
      [activeDrawingLayer]="activeDrawingLayer"
      [drawControls]="drawControls"
      (drawControlsEvent)="addDrawControls($event)"
      (activeLayerChange)="activeDrawingLayer = $event"
    />
  `
})
export class SigtDrawingToolComponent {
  private drawState = inject(DrawState);
  private mapState = inject(MapState);

  get stores(): Stores {
    return this.drawState.stores;
  }
  set stores(stores: Stores) {
    this.drawState.stores = stores;
  }

  get map(): IgoMap {
    return this.mapState.map;
  }

  get drawControls(): DrawControls {
    return this.drawState.drawControls;
  }
  set drawControls(drawControls: DrawControls) {
    this.drawState.drawControls = drawControls;
  }

  get activeDrawingLayer(): CoucheActive {
    return this.drawState.activeDrawingLayer;
  }
  set activeDrawingLayer(layer: CoucheActive) {
    this.drawState.activeDrawingLayer = layer;
  }

  addLayersID(layerID: string): void {
    this.drawState.layersID.push(layerID);
  }

  addDrawControls(drawControls: DrawControls): void {
    this.drawControls = drawControls;
  }
}
